import { useMemo } from 'react';
import { Configuration, OrdersApi } from '@/api';
import { OrderFilters } from '@/api/OrdersApi';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';

export function truncateDate(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function makeQueryRange(startDate: Date, endDate: Date) {
  const startInclusive = truncateDate(startDate);

  const endExclusive = truncateDate(endDate);
  endExclusive.setDate(endExclusive.getDate() + 1);

  return { startInclusive, endExclusive };
}

function useOrderMetricsQuery(
  startDate: Date | null,
  endDate: Date | null,
  currencyCode?: string,
  filters?: OrderFilters,
) {
  const auth = useAuth();

  const ordersApi = useMemo(() => new OrdersApi(new Configuration(auth)), [auth]);

  const enabled = !!startDate && !!endDate && endDate > startDate;

  const query = useQuery({
    queryKey: ['order-metrics', startDate, endDate, currencyCode, filters],
    enabled,
    queryFn: async () => {
      if (!startDate || !endDate) return {};

      const { startInclusive, endExclusive } = makeQueryRange(startDate, endDate);

      const [daily, monthly] = await Promise.all([
        ordersApi.listDailyOrderMetrics(startInclusive, endExclusive, filters, currencyCode),
        ordersApi.listMonthlyOrderMetrics(startInclusive, endExclusive, filters, currencyCode),
      ]);

      return { daily, monthly };
    },
  });

  const daily = query.data?.daily ?? null;

  const totalOrders = daily?.totalCount ?? 0;
  const totalRevenue = daily?.totalAmount ?? (currencyCode ? { amount: 0, currencyCode } : 0);

  const distinctStores = daily?.storeCount ?? 0;
  const distinctSuppliers = daily?.supplierCount ?? 0;

  return {
    daily: query.data?.daily ?? null,
    monthly: query.data?.monthly ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    totalOrderCount: totalOrders,
    totalOrderAmount: totalRevenue,
    storeCount: distinctStores,
    supplierCount: distinctSuppliers,
  };
}

export default useOrderMetricsQuery;
